import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActionLinkPicker } from "@/components/workspace/ActionLinkPicker";
import { PersonSelect, ReviewFields, reviewPayload } from "@/components/workspace/team/WorkFormParts";
import { LEVEL } from "@/lib/format";
import { toast } from "sonner";

const GOLD = "bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]";

export function AssignWorkDialog({ orgId, people, defaultOwner, onClose, onSaved }) {
  const assignable = people.filter((p) => p.in_scope);
  const [f, setF] = useState({
    title: "", description: "", owner_id: defaultOwner || "", due_date: "", priority_level: "normal",
    link: { link_type: "priority", link_id: "" }, requires_review: false, reviewer_id: "", alternate_reviewer_id: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give the work a title");
    if (!f.link.link_id) return toast.error("Choose the priority, improvement or KPI this work delivers");
    if (!f.owner_id) return toast.error("Choose one accountable person");
    if (f.requires_review && !f.reviewer_id) return toast.error("Choose an independent reviewer");
    setSaving(true);
    try {
      await api.post(`/workspace/team/actions?org_id=${orgId}`, {
        title: f.title.trim(), description: f.description || null, owner_id: f.owner_id, due_date: f.due_date || null,
        priority_level: f.priority_level, ...f.link, ...reviewPayload(f),
      });
      toast.success("Work assigned");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="assign-work-dialog">
        <DialogHeader>
          <DialogTitle>Assign work</DialogTitle>
          <DialogDescription>Every piece of work must deliver an approved priority, a Business Health improvement or a KPI.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="assign-title" /></div>
          <div><Label>Details</Label><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="assign-description" /></div>
          <ActionLinkPicker orgId={orgId} value={f.link} onChange={(v) => set("link", v)} testId="assign-link" />
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <PersonSelect label="Accountable person" value={f.owner_id} onChange={(v) => set("owner_id", v)} people={assignable} testId="assign-owner" />
            </div>
            <div><Label>Due date</Label><Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" data-testid="assign-due" /></div>
            <div><Label>Importance</Label>
              <Select value={f.priority_level} onValueChange={(v) => set("priority_level", v)}>
                <SelectTrigger className="mt-1" data-testid="assign-level"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "normal", "high", "critical"].map((l) => <SelectItem key={l} value={l}>{LEVEL[l].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <ReviewFields f={f} set={set} people={people} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className={GOLD} data-testid="assign-save-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Assign work"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
